import { motion } from 'framer-motion'
import style from './HeroBanner.module.css'
import brainVideo from '../../../assets/images/brain.mp4'
import { useNavigate } from 'react-router-dom'
import GuestContext from '../../../contexts/GuestContext'
import { useContext, useCallback, useEffect, useRef } from 'react'

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.25 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
}

const chips = [
  '45,000 companies mapped',
  'Personalized weekly shortlist',
  'No feed · No noise',
]

const HeroBanner = () => {
  const navigate = useNavigate()
  const { setOpenGuestInfo } = useContext(GuestContext)
  const videoRef = useRef(null)

  const handleNavigation = useCallback((path, sectionId) => {
    navigate(path)
    setTimeout(() => {
      const section = document.getElementById(sectionId)
      if (section) section.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  }, [navigate])

  const openRegister = () => {
    if (typeof setOpenGuestInfo === 'function') setOpenGuestInfo(true)
    navigate('/?auth=register')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const tryPlay = () => {
      v.muted = true
      v.play().catch(() => { })
    }
    v.addEventListener('canplay', tryPlay)
    v.addEventListener('ended', tryPlay)
    tryPlay()
    return () => {
      v.removeEventListener('canplay', tryPlay)
      v.removeEventListener('ended', tryPlay)
    }
  }, [])

  return (
    <section className={style.mainContainer} aria-label="Hero banner">
      {/* ── Background video ── */}
      <div className={style.videoWrapper} aria-hidden="true">
        <video
          ref={videoRef}
          className={`${style.backgroundVideo} ${style.fitCover}`}
          src={brainVideo}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        />
        <div className={style.videoDim} />
        <div className={style.videoGlass} />
      </div>

      {/* ── Foreground content ── */}
      <div className={style.container}>
        <motion.div
          className={style.content}
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {/* Eyebrow */}
          <motion.div variants={itemVariants}>
            <span className={style.eyebrow}>AI Career Platform · Sweden</span>
          </motion.div>

          {/* Headline */}
          <motion.h1 className={style.headline} variants={itemVariants}>
            Your career.
            <br />
            <span className={style.gradientText}>Powered by AI.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p className={style.subText} variants={itemVariants}>
            Chimp reads 45,000 companies in Sweden, scores your CV against real jobs,
            and hands you a ranked weekly shortlist no feed, no noise, just what's worth your time.
          </motion.p>

          {/* CTAs */}
          <motion.div className={style.actions} variants={itemVariants}>
            <motion.button
              className={style.primaryBtn}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={openRegister}
            >
              Get started free →
            </motion.button>
            <motion.button
              className={style.secondaryBtn}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => handleNavigation('/', 'bot-hero')}
            >
              See how it works
            </motion.button>
          </motion.div>

          {/* Trust chips */}
          <motion.div className={style.chips} variants={itemVariants}>
            {chips.map((chip) => (
              <span key={chip} className={style.chip}>
                <span className={style.chipDot} />
                {chip}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default HeroBanner
